import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { formatDistanceToNow } from 'date-fns';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Plus, Trash2, Send, Check, AlertCircle, MessageCircle, ChevronDown, ChevronUp, ExternalLink, Paperclip, FileVideo, Film, Image as ImageIcon, Upload, Loader2, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { FileList } from './FileList';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATES = [
  { value: 'requested', label: 'Requested', color: '#999999' },
  { value: 'draft_submitted', label: 'Draft Submitted', color: '#0055FF' },
  { value: 'pending_review', label: 'Pending Review', color: '#0055FF' },
  { value: 'revision_requested', label: 'Revision Requested', color: '#FFB800' },
  { value: 'approved', label: 'Approved', color: '#00C972' },
  { value: 'scheduled', label: 'Scheduled', color: '#9B6EFF' },
  { value: 'posted', label: 'Posted', color: '#FF451A' },
  { value: 'insights_pending', label: 'Insights Pending', color: '#666666' },
  { value: 'completed', label: 'Completed', color: '#111111' },
];

const REVIEWABLE_STATES = ['draft_submitted', 'pending_review', 'revision_requested'];

const stateMeta = (v) => STATES.find((s) => s.value === v) || { label: v, color: '#999999' };

const CONTENT_TYPES = ['Reel', 'Story', 'Post', 'Short', 'Video', 'Live'];

const allowedNextStates = (currentRole, currentState) => {
  if (currentRole === 'brand') return ['approved', 'revision_requested'];
  if (currentRole === 'creator') return ['draft_submitted', 'scheduled', 'posted', 'insights_pending'];
  // managers / admin
  return STATES.map((s) => s.value).filter((s) => s !== currentState);
};

export const DeliverableTracker = ({ campaignId, creatorName, canCreate, focusApprovals, onChange }) => {
  const { user } = useAuth();
  const role = user?.role;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [form, setForm] = useState({ content_type: 'Reel', description: '', due_date: '' });
  const [expanded, setExpanded] = useState(null);

  const [openFeedback, setOpenFeedback] = useState(null); // deliverable id whose feedback dialog is open
  const [feedbackComment, setFeedbackComment] = useState('');
  const [feedbackBusy, setFeedbackBusy] = useState(false);

  const fetch = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/deliverables?campaign_id=${campaignId}`, { withCredentials: true });
      setItems(data || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetch(); /* eslint-disable-next-line */ }, [campaignId]);

  const createDeliverable = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/deliverables`, { campaign_id: campaignId, ...form, due_date: form.due_date || null }, { withCredentials: true });
      toast.success('Deliverable created');
      setOpenCreate(false);
      setForm({ content_type: 'Reel', description: '', due_date: '' });
      fetch(); onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create');
    }
  };

  const updateState = async (id, newState) => {
    setItems((prev) => prev.map((d) => (d.id === id ? { ...d, state: newState } : d)));
    try {
      await axios.patch(`${API_URL}/api/deliverables/${id}`, { state: newState }, { withCredentials: true });
      toast.success(`Moved to ${stateMeta(newState).label}`);
      onChange?.();
      fetch();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
      fetch();
    }
  };

  const updateUrls = async (id, payload) => {
    try {
      await axios.patch(`${API_URL}/api/deliverables/${id}`, payload, { withCredentials: true });
      toast.success('Updated');
      fetch();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const removeDeliverable = async (id) => {
    if (!window.confirm('Delete this deliverable?')) return;
    try {
      await axios.delete(`${API_URL}/api/deliverables/${id}`, { withCredentials: true });
      toast.success('Deleted');
      fetch(); onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const requestRevision = async (id) => {
    const comment = (feedbackComment || '').trim();
    if (!comment) {
      toast.error('Add a short note so the creator knows what to change');
      return;
    }
    setFeedbackBusy(true);
    try {
      await axios.post(
        `${API_URL}/api/deliverables/${id}/feedback`,
        { comment, action: 'revision_requested' },
        { withCredentials: true }
      );
      toast.success('Revision requested — creator notified');
      setOpenFeedback(null);
      setFeedbackComment('');
      fetch();
      onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to request revision');
    } finally {
      setFeedbackBusy(false);
    }
  };

  const displayedItems = focusApprovals
    ? items.filter((d) => REVIEWABLE_STATES.includes(d.state))
    : items;

  return (
    <div data-testid="deliverable-tracker">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-[#0F0F0F] dark:text-white tracking-tight">{focusApprovals ? 'Pending Approvals' : 'Deliverables'}{creatorName && <span className="text-[#6B6B6B] dark:text-zinc-500 font-normal"> · {creatorName}</span>}</h3>
          {focusApprovals && <p className="text-xs text-[#6B6B6B] dark:text-zinc-400 mt-0.5">Drafts awaiting brand/manager review.</p>}
        </div>
        {canCreate && !focusApprovals && (
          <Button onClick={() => setOpenCreate(true)} className="bg-[#FF451A] text-[#0F0F0F] dark:text-white rounded-full px-4 py-2 text-sm hover:bg-[#E63E17] shadow-[0_4px_20px_rgba(255,69,26,0.3)]" data-testid="add-deliverable-button">
            <Plus size={14} className="mr-1" /> Add Deliverable
          </Button>
        )}
      </div>

      {loading ? (
        <div className="py-8 text-center text-sm text-[#6B6B6B] dark:text-zinc-500">Loading…</div>
      ) : displayedItems.length === 0 ? (
        <div className="py-12 text-center text-sm text-[#6B6B6B] dark:text-zinc-500 border-2 border-dashed border-black/[0.10] dark:border-white/10 rounded-xl">
          {focusApprovals ? 'No drafts pending review.' : 'No deliverables yet. Add the first one to start the pipeline.'}
        </div>
      ) : (
        <div className="space-y-3">
          {displayedItems.map((d) => {
            const meta = stateMeta(d.state);
            const isOpen = expanded === d.id;
            const nextStates = allowedNextStates(role, d.state);
            return (
              <div key={d.id} data-deliv-id={d.id} className="border border-black/[0.08] dark:border-white/5 rounded-xl bg-white dark:bg-[#0A0B0D] hover:border-white/10 transition-colors" data-testid="deliverable-row">
                <div className="p-4 flex items-center gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] uppercase tracking-wider text-[#FF451A] font-semibold bg-[#FF451A]/10 border border-[#FF451A]/20 px-2 py-0.5 rounded">{d.content_type}</span>
                      <span
                        className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider"
                        style={{ backgroundColor: `${meta.color}20`, color: meta.color, border: `1px solid ${meta.color}30` }}
                        data-testid="deliverable-state"
                      >
                        {meta.label}
                      </span>
                      {d.due_date && <span className="text-xs text-[#6B6B6B] dark:text-zinc-500 font-mono">due {new Date(d.due_date).toLocaleDateString()}</span>}
                    </div>
                    <p className="text-sm text-[#0F0F0F] dark:text-white font-medium">{d.description}</p>
                    {(d.draft_url || d.posted_url) && (
                      <div className="flex gap-3 mt-1 text-xs">
                        {d.draft_url && <a href={d.draft_url} target="_blank" rel="noreferrer" className="text-[#0055FF] hover:underline flex items-center gap-1"><ExternalLink size={10} /> Draft</a>}
                        {d.posted_url && <a href={d.posted_url} target="_blank" rel="noreferrer" className="text-[#FF451A] hover:underline flex items-center gap-1"><ExternalLink size={10} /> Posted</a>}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    {/* Next state buttons */}
                    {nextStates.length > 0 && (
                      <Select onValueChange={(v) => updateState(d.id, v)}>
                        <SelectTrigger className="w-44 rounded-lg text-xs bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" data-testid="deliverable-state-select">
                          <SelectValue placeholder="Move to…" />
                        </SelectTrigger>
                        <SelectContent>
                          {nextStates.map((s) => (
                            <SelectItem key={s} value={s}>{stateMeta(s).label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    {role === 'brand' && ['draft_submitted', 'pending_review', 'revision_requested'].includes(d.state) && (
                      <>
                        <Button onClick={() => updateState(d.id, 'approved')} className="bg-[#00C972] hover:bg-[#00b362] text-[#0F0F0F] dark:text-white rounded-full text-xs px-3 py-1.5" data-testid="approve-deliverable-button">
                          <Check size={12} className="mr-1" /> Approve
                        </Button>
                        <Button onClick={() => { setOpenFeedback(d.id); setFeedbackComment(''); }} className="border border-[#FFB800]/30 bg-[#FFB800]/10 text-[#FFB800] hover:bg-[#FFB800]/20 rounded-full text-xs px-3 py-1.5" data-testid="request-revision-button">
                          <AlertCircle size={12} className="mr-1" /> Revise
                        </Button>
                      </>
                    )}
                    {canCreate && (
                      <button onClick={() => removeDeliverable(d.id)} className="p-1.5 text-[#6B6B6B] dark:text-zinc-500 hover:text-[#FF3B30] transition-colors" data-testid="delete-deliverable-button">
                        <Trash2 size={14} />
                      </button>
                    )}
                    <button onClick={() => setExpanded(isOpen ? null : d.id)} className="p-1.5 text-[#6B6B6B] dark:text-zinc-500 hover:text-white transition-colors">
                      {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                  </div>
                </div>

                {isOpen && (
                  <DeliverableDetails
                    deliverable={d}
                    role={role}
                    onSaveUrls={(payload) => updateUrls(d.id, payload)}
                    onChanged={fetch}
                    onRequestRevision={() => { setOpenFeedback(d.id); setFeedbackComment(''); }}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={openCreate} onOpenChange={setOpenCreate}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add deliverable</DialogTitle><DialogDescription className="sr-only">Add a new deliverable for the campaign.</DialogDescription></DialogHeader>
          <form onSubmit={createDeliverable} className="space-y-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider block mb-1 text-[#0F0F0F] dark:text-white">Content type</label>
              <Select value={form.content_type} onValueChange={(v) => setForm({ ...form, content_type: v })}>
                <SelectTrigger className="rounded-xl bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" data-testid="deliverable-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CONTENT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider block mb-1 text-[#0F0F0F] dark:text-white">Description</label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required placeholder="30s product reveal reel with trending audio" className="rounded-xl bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" data-testid="deliverable-description-input" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider block mb-1 text-[#0F0F0F] dark:text-white">Due date</label>
              <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="rounded-xl bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" />
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="submit" className="bg-[#FF451A] text-[#0F0F0F] dark:text-white rounded-full px-5 hover:bg-[#E63E17] flex-1" data-testid="create-deliverable-submit">Create</Button>
              <Button type="button" onClick={() => setOpenCreate(false)} className="border border-black/[0.10] dark:border-white/10 bg-transparent text-[#0F0F0F] dark:text-white hover:bg-white/5 rounded-full px-5">Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      {/* Request revision dialog */}
      <Dialog open={!!openFeedback} onOpenChange={(o) => { if (!o) { setOpenFeedback(null); setFeedbackComment(''); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Request revision</DialogTitle><DialogDescription className="sr-only">Request a revision from the creator.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-[#6B6B6B] dark:text-zinc-400">
              The creator and their talent manager will be notified instantly. Be specific — timestamp, frame, wording.
            </p>
            <textarea
              value={feedbackComment}
              onChange={(e) => setFeedbackComment(e.target.value)}
              rows={5}
              placeholder="e.g. The product shot at 0:08 needs better lighting. Caption should mention 'limited edition'."
              className="w-full rounded-xl text-sm p-3 outline-none focus:border-[#FFB800]/40 bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
              data-testid="revision-feedback-input"
            />
            <div className="flex gap-2 pt-1">
              <Button
                onClick={() => requestRevision(openFeedback)}
                disabled={feedbackBusy || !feedbackComment.trim()}
                className="bg-[#FFB800] text-black font-semibold rounded-full px-5 hover:bg-[#E6A700] flex-1 disabled:opacity-40"
                data-testid="submit-revision-button"
              >
                {feedbackBusy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <AlertCircle size={14} className="mr-1.5" />}
                Send revision request
              </Button>
              <Button type="button" onClick={() => { setOpenFeedback(null); setFeedbackComment(''); }} className="border border-black/[0.10] dark:border-white/10 bg-transparent text-[#0F0F0F] dark:text-white hover:bg-white/5 rounded-full px-5">Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const DeliverableDetails = ({ deliverable: d, role, onSaveUrls, onChanged, onRequestRevision }) => {
  const [draftUrl, setDraftUrl] = useState(d.draft_url || '');
  const [postedUrl, setPostedUrl] = useState(d.posted_url || '');
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [newKind, setNewKind] = useState('comment');
  const [loadingC, setLoadingC] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef(null);

  const submissions = Array.isArray(d.submissions) ? [...d.submissions].sort((a, b) => (b.version || 0) - (a.version || 0)) : [];
  const feedbackList = Array.isArray(d.feedback) ? [...d.feedback].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)) : [];
  const latestVersion = submissions[0]?.version || 0;
  const isPendingReview = ['draft_submitted', 'pending_review', 'revision_requested'].includes(d.state);
  const isApproved = d.state === 'approved';

  const fetchComments = async () => {
    setLoadingC(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/deliverables/${d.id}/comments`, { withCredentials: true });
      setComments(data || []);
    } catch {
      // silent
    } finally {
      setLoadingC(false);
    }
  };

  useEffect(() => { fetchComments(); /* eslint-disable-next-line */ }, [d.id]);

  const sendComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    try {
      await axios.post(`${API_URL}/api/deliverables/${d.id}/comments`, { message: newComment, kind: newKind }, { withCredentials: true });
      setNewComment('');
      setNewKind('comment');
      fetchComments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to comment');
    }
  };

  const canEditDraft = role === 'creator';
  const canEditPosted = ['creator', 'manager', 'talent_manager', 'campaign_manager', 'admin', 'agency'].includes(role);
  const canReview = role === 'brand';

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      toast.error('File too large (max 50MB)');
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(`${API_URL}/api/deliverables/${d.id}/upload`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Submission uploaded — brand notified');
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="border-t border-black/[0.08] dark:border-white/5 bg-black/40 p-4 space-y-4">
      {/* Versioned submissions — primary brand-review surface */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-400 font-mono flex items-center gap-1.5">
            <FileVideo size={11} /> Submissions {submissions.length > 0 && <span className="text-[#A1A1AA] dark:text-zinc-600">· v{latestVersion} latest</span>}
          </div>
          {canEditDraft && (
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*"
                onChange={(e) => handleFile(e.target.files?.[0])}
                className="hidden"
                data-testid="deliverable-file-input"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="bg-[#FF451A] text-[#0F0F0F] dark:text-white rounded-full px-3 py-1.5 text-xs hover:bg-[#E63E17] disabled:opacity-40"
                data-testid="upload-submission-button"
              >
                {uploading ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Upload size={12} className="mr-1" />}
                {submissions.length > 0 ? `Upload v${latestVersion + 1}` : 'Upload draft'}
              </Button>
            </div>
          )}
        </div>
        {submissions.length === 0 ? (
          <div className="text-xs text-[#6B6B6B] dark:text-zinc-500 py-4 text-center border border-dashed border-black/[0.10] dark:border-white/10 rounded-lg">
            {canEditDraft ? 'No submissions yet. Upload your first draft above.' : 'Creator has not uploaded a draft yet.'}
          </div>
        ) : (
          <div className="space-y-2">
            {submissions.map((s, i) => {
              const isLatest = i === 0;
              const isVideo = (s.content_type || '').startsWith('video/');
              const isImage = (s.content_type || '').startsWith('image/');
              const Icon = isVideo ? Film : isImage ? ImageIcon : Paperclip;
              return (
                <div
                  key={`${s.version}-${i}`}
                  className={`flex items-center gap-3 p-2.5 rounded-lg border ${isLatest ? 'bg-white/[0.04] border-white/15' : 'bg-white/[0.02] border-black/[0.08] dark:border-white/5'}`}
                  data-testid="deliverable-submission-row"
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${isLatest ? 'bg-[#FF451A]/10 text-[#FF451A]' : 'bg-white/5 text-[#6B6B6B] dark:text-zinc-400'}`}>
                    <Icon size={14} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-semibold text-[#0F0F0F] dark:text-white">v{s.version}</span>
                      {isLatest && <span className="text-[10px] uppercase tracking-wider font-semibold text-[#FF451A] bg-[#FF451A]/10 border border-[#FF451A]/20 px-1.5 py-0.5 rounded">Latest</span>}
                      <span className="text-xs text-zinc-300 truncate">{s.file_name}</span>
                    </div>
                    <div className="text-[10px] text-[#6B6B6B] dark:text-zinc-500 font-mono mt-0.5">
                      by {s.uploaded_by_name || '—'} · {(() => { try { return formatDistanceToNow(new Date(s.uploaded_at), { addSuffix: true }); } catch { return ''; } })()}
                      {s.size_bytes ? ` · ${(s.size_bytes / (1024 * 1024)).toFixed(1)} MB` : ''}
                    </div>
                  </div>
                  {s.file_url && (
                    <a
                      href={s.file_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-[#0055FF] hover:underline flex items-center gap-1 shrink-0"
                      data-testid="submission-open-link"
                    >
                      <ExternalLink size={11} /> Open
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Inline brand review actions on the latest submission (or any attached file via legacy uploader) */}
        {canReview && isPendingReview && (
          <div className="mt-3 flex items-center gap-2 p-3 rounded-xl bg-[#0055FF]/5 border border-[#0055FF]/20">
            <Clock size={14} className="text-[#0055FF] shrink-0" />
            <span className="text-xs text-zinc-300 flex-1">Reviewing {latestVersion > 0 ? <span className="font-semibold text-[#0F0F0F] dark:text-white">v{latestVersion}</span> : <span className="text-[#6B6B6B] dark:text-zinc-400">attached file</span>} — your call.</span>
            <Button
              onClick={() => onSaveUrls({ state: 'approved' })}
              className="bg-[#00C972] hover:bg-[#00b362] text-[#0F0F0F] dark:text-white rounded-full text-xs px-3 py-1.5"
              data-testid="approve-from-details-button"
            >
              <Check size={12} className="mr-1" /> Approve
            </Button>
            <Button
              onClick={onRequestRevision}
              className="border border-[#FFB800]/30 bg-[#FFB800]/10 text-[#FFB800] hover:bg-[#FFB800]/20 rounded-full text-xs px-3 py-1.5"
              data-testid="revise-from-details-button"
            >
              <AlertCircle size={12} className="mr-1" /> Request revision
            </Button>
          </div>
        )}

        {isApproved && d.approved_by_name && (
          <div className="mt-3 text-xs text-[#00C972] flex items-center gap-1.5 bg-[#00C972]/5 border border-[#00C972]/20 rounded-lg px-3 py-2" data-testid="deliverable-approved-banner">
            <Check size={13} /> Approved by <span className="font-semibold">{d.approved_by_name}</span>
            {d.approved_at && (
              <span className="text-[#6B6B6B] dark:text-zinc-500 font-mono ml-1">
                · {(() => { try { return formatDistanceToNow(new Date(d.approved_at), { addSuffix: true }); } catch { return ''; } })()}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Feedback log */}
      {feedbackList.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-400 mb-2 font-mono flex items-center gap-1.5">
            <AlertCircle size={11} /> Revision feedback
          </div>
          <div className="space-y-2">
            {feedbackList.map((f, i) => (
              <div key={i} className="bg-[#FFB800]/5 border border-[#FFB800]/15 rounded-lg p-3" data-testid="deliverable-feedback-row">
                <div className="flex items-baseline justify-between gap-2 mb-1">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-semibold text-[#0F0F0F] dark:text-white">{f.sender_name || '—'}</span>
                    <span className="text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500">{(f.sender_role || '').replace('_', ' ')}</span>
                    {f.version != null && <span className="text-[10px] uppercase tracking-wider text-[#FFB800] bg-[#FFB800]/10 border border-[#FFB800]/20 px-1.5 py-0.5 rounded">on v{f.version}</span>}
                  </div>
                  <span className="text-[10px] text-[#6B6B6B] dark:text-zinc-500 font-mono">{(() => { try { return formatDistanceToNow(new Date(f.created_at), { addSuffix: true }); } catch { return ''; } })()}</span>
                </div>
                <p className="text-sm text-zinc-200 whitespace-pre-wrap">{f.comment}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <FileList attachedTo="deliverable" attachedId={d.id} canDelete={canEditDraft} />

      {/* Reference links — always visible to every role (read-only view) */}
      {(d.draft_url || d.posted_url) && (
        <div data-testid="deliverable-reference-links">
          <div className="text-xs uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-400 mb-2 font-mono flex items-center gap-1.5">
            <ExternalLink size={11} /> Reference links
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {d.draft_url && (
              <a
                href={d.draft_url}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-2 p-3 rounded-lg bg-[#0055FF]/5 border border-[#0055FF]/20 hover:bg-[#0055FF]/10 transition-colors group"
                data-testid="deliverable-draft-url-link"
              >
                <div className="w-7 h-7 rounded-lg bg-[#0055FF]/15 flex items-center justify-center text-[#0055FF] shrink-0">
                  <Paperclip size={13} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-[#0055FF]">Draft URL</div>
                  <div className="text-xs text-zinc-300 truncate group-hover:text-white transition-colors">{d.draft_url}</div>
                </div>
                <ExternalLink size={12} className="text-[#6B6B6B] dark:text-zinc-500 group-hover:text-[#0055FF] transition-colors shrink-0" />
              </a>
            )}
            {d.posted_url && (
              <a
                href={d.posted_url}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-2 p-3 rounded-lg bg-[#FF451A]/5 border border-[#FF451A]/20 hover:bg-[#FF451A]/10 transition-colors group"
                data-testid="deliverable-posted-url-link"
              >
                <div className="w-7 h-7 rounded-lg bg-[#FF451A]/15 flex items-center justify-center text-[#FF451A] shrink-0">
                  <ExternalLink size={13} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-[#FF451A]">Posted URL</div>
                  <div className="text-xs text-zinc-300 truncate group-hover:text-white transition-colors">{d.posted_url}</div>
                </div>
                <ExternalLink size={12} className="text-[#6B6B6B] dark:text-zinc-500 group-hover:text-[#FF451A] transition-colors shrink-0" />
              </a>
            )}
          </div>
        </div>
      )}

      {/* Editable URL inputs — only for roles that can change them */}
      {(canEditDraft || canEditPosted) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {canEditDraft && (
            <div>
              <label className="text-xs uppercase tracking-wider block mb-1 font-mono text-[#0F0F0F] dark:text-white">Draft URL (creator)</label>
              <div className="flex gap-2">
                <Input value={draftUrl} onChange={(e) => setDraftUrl(e.target.value)} placeholder="https://drive.google.com/..." className="rounded-lg text-xs bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" />
                <Button onClick={() => onSaveUrls({ draft_url: draftUrl })} className="bg-[#0055FF] text-[#0F0F0F] dark:text-white text-xs rounded-lg px-3 hover:bg-[#0044CC]">Save</Button>
              </div>
            </div>
          )}
          {canEditPosted && (
            <div>
              <label className="text-xs uppercase tracking-wider block mb-1 font-mono text-[#0F0F0F] dark:text-white">Posted URL</label>
              <div className="flex gap-2">
                <Input value={postedUrl} onChange={(e) => setPostedUrl(e.target.value)} placeholder="https://instagram.com/p/..." className="rounded-lg text-xs bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" />
                <Button onClick={() => onSaveUrls({ posted_url: postedUrl })} className="bg-[#FF451A] text-[#0F0F0F] dark:text-white text-xs rounded-lg px-3 hover:bg-[#E63E17]">Save</Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* State history */}
      {d.state_history && d.state_history.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-400 mb-2 font-mono">State history</div>
          <div className="flex flex-wrap gap-1.5">
            {d.state_history.map((h, i) => (
              <span key={i} className="text-[10px] bg-white/5 border border-black/[0.10] dark:border-white/10 rounded-full px-2 py-0.5 text-[#6B6B6B] dark:text-zinc-400">
                {stateMeta(h.state).label} <span className="text-[#A1A1AA] dark:text-zinc-600">· {h.by || '—'}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Approval thread */}
      <div>
        <div className="text-xs uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-400 mb-2 flex items-center gap-1 font-mono"><MessageCircle size={11} /> Approval thread</div>
        <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
          {loadingC ? (
            <div className="text-xs text-[#6B6B6B] dark:text-zinc-500 py-3">Loading…</div>
          ) : comments.length === 0 ? (
            <div className="text-xs text-[#6B6B6B] dark:text-zinc-500 py-3">No comments yet.</div>
          ) : (
            comments.map((c) => (
              <div key={c.id} className="bg-white/[0.03] rounded-lg p-3 border border-black/[0.08] dark:border-white/5" data-testid="approval-comment">
                <div className="flex items-baseline justify-between gap-2 mb-1">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-semibold text-[#0F0F0F] dark:text-white">{c.user_name}</span>
                    <span className="text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500">{c.user_role?.replace('_', ' ')}</span>
                    {c.kind !== 'comment' && (
                      <span className={`text-[10px] uppercase font-semibold tracking-wider px-1.5 py-0.5 rounded ${c.kind === 'approval' ? 'bg-[#00C972]/20 text-[#00C972] border border-[#00C972]/30' : 'bg-[#FFB800]/20 text-[#FFB800] border border-[#FFB800]/30'}`}>
                        {c.kind.replace('_', ' ')}
                      </span>
                    )}
                  </div>
                  <span className="text-[10px] text-[#6B6B6B] dark:text-zinc-500 font-mono">{(() => { try { return formatDistanceToNow(new Date(c.created_at), { addSuffix: true }); } catch { return ''; } })()}</span>
                </div>
                <p className="text-sm text-zinc-200 whitespace-pre-wrap">{c.message}</p>
              </div>
            ))
          )}
        </div>

        <form onSubmit={sendComment} className="mt-3 flex items-center gap-2">
          <Select value={newKind} onValueChange={setNewKind}>
            <SelectTrigger className="w-36 rounded-lg text-xs bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="comment">Comment</SelectItem>
              {role === 'brand' && <SelectItem value="approval">Approval</SelectItem>}
              {role === 'brand' && <SelectItem value="revision_request">Revision request</SelectItem>}
            </SelectContent>
          </Select>
          <Input value={newComment} onChange={(e) => setNewComment(e.target.value)} placeholder="Add a comment, timestamp annotation, or feedback…" className="flex-1 rounded-lg text-sm bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" data-testid="comment-input" />
          <Button type="submit" className="bg-[#FF451A] text-[#0F0F0F] dark:text-white rounded-lg text-sm px-3 hover:bg-[#E63E17]" data-testid="send-comment-button">
            <Send size={13} />
          </Button>
        </form>
      </div>
    </div>
  );
};
